import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import RichTextEditor from '../../ui/RichTextEditor/RichTextEditor';
import { useAuth } from '../../../firebase/AuthProvider';
import Sprint from '../../../interfaces/sprint/Sprint';

interface CloseSprintModalProps {
    show: boolean;
    onHide: () => void;
    sprintToClose: Sprint | undefined;
    onSprintClose: (summaryNotes: string, newSprint: { name: string; startDate: string; endDate: string }) => void;
}

const CloseSprintModal: React.FC<CloseSprintModalProps> = ({ show, onHide, sprintToClose, onSprintClose }) => {
    const [summaryNotes, setSummaryNotes] = useState('');
    const [newSprintName, setNewSprintName] = useState('');
    const [newSprintStartDate, setNewSprintStartDate] = useState('');
    const [newSprintEndDate, setNewSprintEndDate] = useState('');
    const [errors, setErrors] = useState<{ newSprintName?: string; newSprintStartDate?: string; newSprintEndDate?: string }>({});

    const handleSubmit = () => {
        const newErrors: { newSprintName?: string; newSprintStartDate?: string; newSprintEndDate?: string } = {};
        if (!newSprintName.trim()) {
            newErrors.newSprintName = 'Sprint name is required';
        }
        if (!newSprintStartDate) {
            newErrors.newSprintStartDate = 'Start date is required';
        }
        if (!newSprintEndDate) {
            newErrors.newSprintEndDate = 'End date is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        onSprintClose(summaryNotes, {
            name: newSprintName,
            startDate: new Date(newSprintStartDate).toISOString(),
            endDate: new Date(newSprintEndDate).toISOString(),
        });
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Close Sprint: {sprintToClose?.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Sprint Summary / Retrospective Notes</Form.Label>
                        <RichTextEditor
                            value={summaryNotes}
                            onChange={setSummaryNotes}
                            placeholder="Add a summary..."
                        />
                    </Form.Group>
                    <hr />
                    <h4>Create New Sprint</h4>
                    <Form.Group className="mb-3">
                        <Form.Label>New Sprint Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={newSprintName}
                            onChange={e => setNewSprintName(e.target.value)}
                            isInvalid={!!errors.newSprintName}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.newSprintName}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={newSprintStartDate}
                            onChange={e => setNewSprintStartDate(e.target.value)}
                            isInvalid={!!errors.newSprintStartDate}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.newSprintStartDate}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>End Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={newSprintEndDate}
                            onChange={e => setNewSprintEndDate(e.target.value)}
                            isInvalid={!!errors.newSprintEndDate}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.newSprintEndDate}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cancel</Button>
                <Button variant="primary" onClick={handleSubmit}>Close Sprint and Create New</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CloseSprintModal;
